import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Grid,
  Header,
  List,
  Icon,
  Dropdown,
  Tab,
} from "semantic-ui-react";
import { DatePicker } from "@components/DatePicker";
import { DateTime } from "luxon";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import OverdueBooksTab from "./OverdueBooksTab";
import OutstandingFinesTab from "./OutstandingFinesTab";
import UnreturnedMaterialsTab from "./UnreturnedMaterialsTab";

class OverdueDocumentsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fromDate: "",
      toDate: "",
      isExporting: false,
      exportFormat: "csv",
      activeTabIndex: 0,
      overdueBooks: [],
      outstandingFines: [],
      unreturnedMaterials: [],
    };
  }

  componentDidMount() {
    // The individual tab components will handle their own data fetching
    this.fetchDocuments();
  }

  fetchDocuments() {
    const { fromDate, toDate } = this.state;
    const { fetchOverdueDocuments } = this.props;
    if (fetchOverdueDocuments) {
      fetchOverdueDocuments(fromDate, toDate);
    }
  }

  handleFromDateChange = (value) => {
    this.setState({ fromDate: value }, () => {
      this.fetchDocuments();
    });
  };

  handleToDateChange = (value) => {
    this.setState({ toDate: value }, () => {
      this.fetchDocuments();
    });
  };

  handleTabChange = (e, { activeIndex }) => {
    this.setState({ activeTabIndex: activeIndex });
  };

  handleOverdueBooksDataChange = (data) => {
    this.setState({ overdueBooks: data });
  };

  handleOutstandingFinesDataChange = (data) => {
    this.setState({ outstandingFines: data });
  };

  handleUnreturnedMaterialsDataChange = (data) => {
    this.setState({ unreturnedMaterials: data });
  };

  hasDataForCurrentTab = () => {
    const {
      activeTabIndex,
      overdueBooks,
      outstandingFines,
      unreturnedMaterials,
    } = this.state;
    switch (activeTabIndex) {
      case 0:
        return overdueBooks && overdueBooks.length > 0;
      case 1:
        return outstandingFines && outstandingFines.length > 0;
      case 2:
        return unreturnedMaterials && unreturnedMaterials.length > 0;
      default:
        return false;
    }
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  };

  getDateRangeText = () => {
    const { fromDate, toDate } = this.state;
    if (fromDate && toDate) {
      return `${this.formatDate(fromDate)} to ${this.formatDate(toDate)}`;
    } else if (fromDate) {
      return `From ${this.formatDate(fromDate)}`;
    } else if (toDate) {
      return `Until ${this.formatDate(toDate)}`;
    }
    return "All dates";
  };

  handleExport = () => {
    const { exportFormat, activeTabIndex } = this.state;
    if (exportFormat === "csv") {
      this.handleExportCSV(activeTabIndex);
    } else if (exportFormat === "pdf") {
      this.handleExportPDF(activeTabIndex);
    }
  };

  handleExportCSV = (tabIndex) => {
    this.setState({ isExporting: true });
    try {
      let headers = [];
      let data = [];
      let filename = "";

      switch (tabIndex) {
        case 0: {
          headers = [
            "Book Title",
            "Author",
            "ISBN",
            "Call Number",
            "Accession Number",
            "Borrower Name",
            "Borrower Contact",
            "Due Date",
            "Days Overdue",
          ];
          data = this.state.overdueBooks;
          filename = "overdue-books-report";
          break;
        }
        case 1: {
          // Outstanding Fines
          headers = [
            "Patron Name",
            "Patron Email",
            "Total Fine Amount",
            "Payment Status",
            "Number of Fines",
          ];
          data = this.state.outstandingFines.map((patron) => ({
            patronName: patron.patronName,
            patronEmail: patron.patronEmail,
            totalFineAmount: patron.totalFineAmount,
            paymentStatus: patron.paymentStatus,
            numberOfFines: patron.fineBreakdown.length,
          }));
          filename = "outstanding-fines-report";
          break;
        }
        case 2: {
          // Unreturned Materials
          headers = [
            "Patron Name",
            "Patron Email",
            "Number of Unreturned Books",
            "Total Fine Amount",
          ];
          data = this.state.unreturnedMaterials.map((patron) => ({
            patronName: patron.patronName,
            patronEmail: patron.patronEmail,
            numberOfBooks: patron.unreturnedBooks.length,
            totalFineAmount: patron.totalFineAmount,
          }));
          filename = "unreturned-materials-report";
          break;
        }
        default:
          throw new Error("Invalid tab index");
      }

      const csvContent = [
        headers.join(","),
        ...data.map((item) =>
          headers
            .map((header) => {
              let value = "";

              switch (header) {
                case "Book Title":
                  value = item.bookTitle || "";
                  break;
                case "Author":
                  value = item.author || "";
                  break;
                case "ISBN":
                  value = item.isbn || "";
                  break;
                case "Call Number":
                  value = item.callNumber || "";
                  break;
                case "Accession Number":
                  value = item.itemBarcode || "";
                  break;
                case "Borrower Name":
                  value = item.borrowerName || "";
                  break;
                case "Borrower Contact":
                  value = `${item.borrowerContact || ""} / ${
                    item.borrowerPhone || ""
                  }`;
                  break;
                case "Due Date":
                  value = item.dueDate || "";
                  break;
                case "Days Overdue":
                  value = item.overdueDays || 0;
                  break;
                default: {
                  const key = header.toLowerCase().replace(/[^a-z0-9]/g, "");
                  value =
                    item[
                      Object.keys(item).find(
                        (k) => k.toLowerCase().replace(/[^a-z0-9]/g, "") === key
                      )
                    ] || "";
                  break;
                }
              }

              return `"${String(value).replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${filename}-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export CSV failed:", error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  handleExportPDF = (tabIndex) => {
    this.setState({ isExporting: true });
    try {
      let reportTitle = "";
      let tableHeaders = "";
      let tableRows = "";
      let reportType = "default";

      switch (tabIndex) {
        case 0: {
          // Overdue Books
          reportTitle = "Overdue Books Report";
          reportType = "default";
          tableHeaders = `
            <th>#</th>
            <th>Book Title</th>
            <th>Author</th>
            <th>ISBN</th>
            <th>Call Number</th>
            <th>Accession Number</th>
            <th>Borrower Name</th>
            <th>Borrower Contact</th>
            <th>Due Date</th>
            <th>Days Overdue</th>
          `;
          tableRows = this.state.overdueBooks
            .map(
              (book, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${book.bookTitle || ""}</td>
              <td>${book.author || ""}</td>
              <td>${book.isbn || ""}</td>
              <td>${book.callNumber || ""}</td>
              <td>${book.itemBarcode || ""}</td>
              <td>${book.borrowerName || ""}</td>
              <td>${book.borrowerContact || ""}<br/>${
                book.borrowerPhone || ""
              }</td>
              <td>${this.formatDate(book.dueDate)}</td>
              <td>${book.overdueDays || 0}</td>
            </tr>
          `
            )
            .join("");
          break;
        }
        case 1: {
          // Outstanding Fines
          reportTitle = "Outstanding Fines Report";
          reportType = "finemanagement";
          tableHeaders = `
            <th>#</th>
            <th>Patron Name</th>
            <th>Email</th>
            <th>Total Fine Amount</th>
            <th>Payment Status</th>
            <th>Number of Fines</th>
          `;
          tableRows = this.state.outstandingFines
            .map(
              (patron, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${patron.patronName || ""}</td>
              <td>${patron.patronEmail || ""}</td>
              <td>${this.formatCurrency(patron.totalFineAmount || 0)}</td>
              <td>${patron.paymentStatus || ""}</td>
              <td>${patron.fineBreakdown?.length || 0}</td>
            </tr>
          `
            )
            .join("");
          break;
        }
        case 2: {
          // Unreturned Materials
          reportTitle = "Unreturned Materials & Overdue Fines Report";
          reportType = "default";
          tableHeaders = `
            <th>#</th>
            <th>Patron Name</th>
            <th>Email</th>
            <th>Unreturned Books</th>
            <th>Total Fine Amount</th>
          `;
          tableRows = this.state.unreturnedMaterials
            .map(
              (patron, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${patron.patronName || ""}</td>
              <td>${patron.patronEmail || ""}</td>
              <td>${patron.unreturnedBooks?.length || 0}</td>
              <td>${this.formatCurrency(patron.totalFineAmount || 0)}</td>
            </tr>
          `
            )
            .join("");
          break;
        }
      }

      // Use the generateExportHTML template
      const htmlContent = generateExportHTML({
        reportTitle,
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        footerText:
          "Library Information Management System - Overdue Documents Report",
        fallbackLogoText: "Library Resource Center",
        showUniversityLogo: true,
        showLrcLogo: true,
        reportType: reportType,
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.focus();
          }, 500);
        };
      }
    } catch (error) {
      console.error("Export PDF failed:", error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  renderDateRangePicker() {
    const { fromDate, toDate, isExporting, exportFormat } = this.state;
    const hasData = this.hasDataForCurrentTab();

    const exportOptions = [
      { key: "csv", value: "csv", text: "CSV", icon: "table" },
      { key: "pdf", value: "pdf", text: "PDF", icon: "file pdf" },
    ];

    const trigger = (
      <span>
        <Icon
          name={exportFormat === "csv" ? "table" : "file pdf"}
          style={{ marginRight: "0.5em" }}
        />
        {exportFormat.toUpperCase()}
      </span>
    );

    return (
      <>
        <Header as="h3">
          Pick range of dates
          <Header.Subheader style={{ color: "black", marginTop: "10px" }}>
            Choose between which dates to search for overdue documents and
            fines.
          </Header.Subheader>
        </Header>

        <Grid>
          <Grid.Row>
            <Grid.Column width={11}>
              <List horizontal>
                <List.Item>
                  <List.Content>
                    <DatePicker
                      maxDate={toDate}
                      placeholder="From Date"
                      handleDateChange={this.handleFromDateChange}
                    />
                  </List.Content>
                </List.Item>
                <List.Item>
                  <List.Content>
                    <DatePicker
                      minDate={fromDate}
                      placeholder="To Date"
                      handleDateChange={this.handleToDateChange}
                    />
                  </List.Content>
                </List.Item>
              </List>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Button.Group>
                <Dropdown
                  trigger={trigger}
                  button
                  basic
                  floating
                  options={exportOptions}
                  value={exportFormat}
                  icon={null}
                  onChange={(e, { value }) =>
                    this.setState({ exportFormat: value })
                  }
                  selectOnBlur={false}
                />
                <Button
                  primary
                  icon
                  size="small"
                  labelPosition="left"
                  onClick={this.handleExport}
                  loading={isExporting}
                  disabled={!hasData || isExporting}
                  data-test="export-button"
                >
                  <Icon name="download" />
                  Export Results
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </>
    );
  }

  render() {
    const {
      activeTabIndex,
      fromDate,
      toDate,
      overdueBooks,
      outstandingFines,
      unreturnedMaterials,
    } = this.state;

    const panes = [
      {
        menuItem: {
          key: "overdue-books",
          icon: "book",
          content:
            overdueBooks?.length > 0
              ? `Overdue Books (${overdueBooks.length})`
              : "Overdue Books",
        },
        render: () => (
          <Tab.Pane>
            {this.renderDateRangePicker()}
            <OverdueBooksTab
              fromDate={fromDate}
              toDate={toDate}
              onDataChange={this.handleOverdueBooksDataChange}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "outstanding-fines",
          icon: "money bill alternate",
          content:
            outstandingFines?.length > 0
              ? `Outstanding Fines (${outstandingFines.length})`
              : "Outstanding Fines",
        },
        render: () => (
          <Tab.Pane>
            {this.renderDateRangePicker()}
            <OutstandingFinesTab
              fromDate={fromDate}
              toDate={toDate}
              onDataChange={this.handleOutstandingFinesDataChange}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "unreturned-materials",
          icon: "clipboard list",
          content:
            unreturnedMaterials?.length > 0
              ? `Unreturned Materials (${unreturnedMaterials.length})`
              : "Unreturned Materials",
        },
        render: () => (
          <Tab.Pane>
            {this.renderDateRangePicker()}
            <UnreturnedMaterialsTab
              fromDate={fromDate}
              toDate={toDate}
              onDataChange={this.handleUnreturnedMaterialsDataChange}
            />
          </Tab.Pane>
        ),
      },
    ];

    return (
      <div>
        <Tab
          menu={{
            secondary: true,
            pointing: true,
            style: { marginBottom: "2rem" },
          }}
          panes={panes}
          activeIndex={activeTabIndex}
          onTabChange={this.handleTabChange}
        />
      </div>
    );
  }
}

OverdueDocumentsList.propTypes = {
  fetchOverdueDocuments: PropTypes.func,
};

OverdueDocumentsList.defaultProps = {
  fetchOverdueDocuments: () => {},
};

export default OverdueDocumentsList;
